import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from '../../../../interfaces/company.interface';
import { pdfStyles } from './pdfStyles';

interface Props {
  company: Company;
}

export const PdfCompanyTitle = ({ company }: Props) => {
  const { t } = useTranslation();
  const isBranch = useMemo(() => {
    if ('branchSerial' in company) {
      return Number(company?.branchSerial || 0) > 0;
    }

    return false;
  }, [company]);

  const allPhones = useMemo(
    () =>
      [company?.phoneNumber, company?.extraInfo?.phone2, company?.extraInfo?.phone3, company?.extraInfo?.phone4]
        .filter(Boolean)
        .join(', '),
    [company?.phoneNumber, company?.extraInfo],
  );

  const contactLine = useMemo(
    () =>
      [
        allPhones ? `${t('PDF.PHONE_NUMBER')}: ${allPhones}` : null,
        company?.email ? `${t('PDF.EMAIL')}: ${company.email}` : null,
      ]
        .filter(Boolean)
        .join(' - '),
    [allPhones, company?.email, t],
  );

  const getBranchAddress = useCallback(
    (prefix: string, websiteUrl?: string) => {
      const parts = [
        `${prefix}: ${company?.address} ${company?.city} ${company?.postalCode}`,
        websiteUrl ?? null,
      ];
      return (
        <View style={styles.container2}>
          <Text style={styles.body}>{parts.filter(Boolean).join(' - ')}</Text>
        </View>
      );
    },
    [company?.address, company?.city, company?.postalCode],
  );

  const getMasterAddress = useCallback(() => {
    if (company?.parentCompany) {
      return (
        <View style={styles.container2}>
          <Text style={styles.body}>{`${t(
            'PDF.MASTER_COMPANY_TITLE',
          )}: ${company?.parentCompany?.address} ${company?.parentCompany?.city} ${company?.parentCompany?.postalCode}`}</Text>
        </View>
      );
    }
  }, [company?.parentCompany, t]);

  const getCompanyAddress = useCallback(() => {
    if (isBranch) {
      return (
        <View>
          {getBranchAddress(t('PDF.BRANCH_COMPANY_TITLE'), company?.websiteUrl)}
          {getMasterAddress()}
        </View>
      );
    }

    return getBranchAddress(t('PDF.ADDRESS'), company?.websiteUrl);
  }, [getBranchAddress, getMasterAddress, isBranch, t, company?.websiteUrl]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{company?.title}</Text>
      <View style={[styles.container2, { width: 360 }]}>
        <Text style={[styles.body, pdfStyles.maxLines2]}>{company?.profession}</Text>
      </View>
      <View style={styles.container2}>
        <Text style={styles.body}>
          {t('PDF.VAT_NUMBER')}: {company?.vatNumber}
          {' - '}
        </Text>
        <Text style={styles.body}>
          {t('PDF.DOY')}: {company?.doy?.name}
        </Text>
      </View>
      {getCompanyAddress()}
      {contactLine && (
        <View style={styles.container2}>
          <Text style={styles.body}>{contactLine}</Text>
        </View>
      )}
      <View style={styles.container2}>
        {company?.gemiInfo && (
          <Text style={styles.body}>
            {t('PDF.GEMI_NUMBER')}: {company?.gemiInfo}
          </Text>
        )}
        {company?.menoNumber && (
          <Text style={styles.body}>
            {' - '} {t('PDF.MENO_NUMBER')}: {company?.menoNumber}
          </Text>
        )}
        {company?.eaeNumber && (
          <Text style={styles.body}>
            {t('PDF.EAE_NUMBER')}: {company?.eaeNumber}
          </Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    width: 500,
  },
  title: {
    fontFamily: 'Manrope',
    fontWeight: 'bold',
  },
  container2: {
    flexDirection: 'row',
  },
  body: {
    fontSize: 8,
  },
});
